import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { AUTH_PATH } from '@repo/constants';
import { checkIsAosDevice } from '@repo/utils';
import { t } from 'i18next';
import styled from 'styled-components';

import { useKaKaoLogin } from 'hooks/useKaKaoLogin';
import { usePreventDuplicateCall } from 'hooks/usePreventDuplicateCall';
import { checkIsLogin, socialLoginWith } from 'utils/auth';

import ExternalLink from 'components/link/ExternalLink';
import Flex from 'components/util/Flex';

import appleLogo from 'assets/logo/logo-apple.svg';
import googleLogo from 'assets/logo/logo-google.svg';
import kakaoLogo from 'assets/logo/logo-kakao.svg';

import { PATH } from 'routers/paths';

/**
 * AOS device만 접근 가능한 페이지 (딥링크 이슈로 인해 별도의 로그인 페이지를 구현)
 */
export default function SignInPage() {
  const isAosDevice = checkIsAosDevice();

  const navigate = useNavigate();
  const { isProcessing: isInLoginProcess, preventDuplicateCall } = usePreventDuplicateCall();

  const { handleSignInWithKakao } = useKaKaoLogin();

  const handleGoogleLogin = async () => {
    await preventDuplicateCall(async () => {
      await socialLoginWith('Google');
    });
  };

  const handleAppleLogin = async () => {
    await preventDuplicateCall(async () => {
      await socialLoginWith('Apple');
    });
  };
  const handleKaKaoLogin = async () => {
    await preventDuplicateCall(async () => {
      await handleSignInWithKakao();
    });
  };

  useEffect(() => {
    // 로그인 인증 후에, 이벤트를 dispatch한 것을 수신하기 위한 구독 함수
    const subscribeLocalStorageForLoggedIn = () => {
      const isLogin = checkIsLogin();
      if (isLogin) {
        navigate(PATH.DASHBOARD);
      }
    };

    window.addEventListener('loggedIn', subscribeLocalStorageForLoggedIn);

    return () => {
      window.removeEventListener('loggedIn', subscribeLocalStorageForLoggedIn);
    };
  }, []);

  return (
    <SPageWrapper>
      <Flex direction="column" align="center" isExpanded>
        <SAuthButtonListWrapper direction="column" align="center">
          <STitle>Login</STitle>
          <SKakaoBtn type="button" onClick={handleKaKaoLogin} disabled={isInLoginProcess}>
            <img src={kakaoLogo} alt="" />
            <span>
              <Trans i18nKey="login.account_kakao" />
            </span>
          </SKakaoBtn>
          <SGoogleBtn type="button" onClick={handleGoogleLogin} disabled={isInLoginProcess}>
            <img src={googleLogo} alt="" />
            <span>
              <Trans i18nKey="login.account_google" />
            </span>
          </SGoogleBtn>
          <SAppleBtn type="button" onClick={handleAppleLogin} disabled={isInLoginProcess}>
            <img src={appleLogo} alt="" />
            <span>
              <Trans i18nKey="login.account_apple" />
            </span>
          </SAppleBtn>
        </SAuthButtonListWrapper>
        <SRedirectToLoginWrapper>
          {t('login.account_member')}
          <Link
            to={PATH.AUTH.SIGN_IN_WITH_PASSWORD}
            rel="nosublink"
            aria-label={t('general.login')}
            // path={PATH.AUTH.SIGN_IN_PASSWORD + `${callbackUrl ? `?callbackUrl=${callbackUrl}` : ''}`}
            // props={{ state, 'aria-label': t('general.login') }}
          >
            {t('general.login')}
          </Link>
        </SRedirectToLoginWrapper>
        <SAdditionalInformationWrapper>
          <Trans
            i18nKey="login.account_agree_policy"
            components={{
              link1: <ExternalLink to={`${PATH.POLICY.INDEX}#terms`} />,
              link2: <ExternalLink to={`${PATH.POLICY.INDEX}#privacy`} />,
            }}
          />
        </SAdditionalInformationWrapper>
      </Flex>
    </SPageWrapper>
  );
}

const SPageWrapper = styled.section`
  width: 100%;
  height: calc(100vh - 72px - 120px);
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SAuthButtonListWrapper = styled(Flex)`
  padding: 0 11.5px;
  width: 100%;
  box-sizing: border-box;
`;

const STitle = styled.h2`
  margin-bottom: 52px;
  height: 38px;
  font-size: 28px;
  font-weight: 500;
`;

const SLoginButton = styled.button`
  position: relative;
  max-width: 360px;
  width: 100%;
  height: 52px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  font-size: 15px;
  text-align: left;

  & + & {
    margin-top: 16px;
  }
  & > img {
    position: absolute;
    left: 14px;
    margin-right: 8px;
    width: 24px;
    height: 24px;
  }
  & strong {
    font-weight: 600;
  }
`;

const SKakaoBtn = styled(SLoginButton)`
  background-color: #ffe812;
  color: #3c1e1e;
`;

const SGoogleBtn = styled(SLoginButton)`
  border: #d8d8d8 1px solid;
`;
const SAppleBtn = styled(SLoginButton)`
  background-color: #000000;
  color: white;
`;

const SRedirectToLoginWrapper = styled.div`
  margin-top: 28px;
  font-size: 14px;
  color: #131415;
  & > a {
    margin-left: 16px;
    font-weight: 700;
    line-height: 17px;
    text-decoration: underline;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SAdditionalInformationWrapper = styled.div`
  margin-top: 32px;
  width: 220px;
  color: #bcbcbc;
  text-align: center;
  font-size: 11px;
  font-weight: 400;
  line-height: 20px;
`;
